"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook to handle token storage from URL parameters
 * Stores tokens in localStorage and keeps them in URL query params
 * Returns whether tokens are available (either from URL or already in localStorage)
 */
export function useTokenStorage(): boolean {
	const [searchParams] = useSearchParams();
	
	// Always start with false to match SSR (no localStorage during SSR)
	const [tokensReady, setTokensReady] = useState(false);

	useEffect(() => {
		// Check if tokens already exist in localStorage (client-side only after hydration)
		if (typeof window !== "undefined") {
			const existingToken = localStorage.getItem("accessToken");
			if (existingToken) {
				setTokensReady(true);
			}
		}

		// Check for tokens in URL (from callback route)
		const token = searchParams.get("_token");
		const refreshToken = searchParams.get("_refresh");

		if (token) {
			// Store tokens in localStorage
			if (typeof window !== "undefined") {
				localStorage.setItem("accessToken", token);
				if (refreshToken) {
					localStorage.setItem("refreshToken", refreshToken);
				}
				
				// Mark tokens as ready immediately after storing
				setTokensReady(true);
				
				// Keep tokens in URL query params - don't remove them
				// Dispatch event to notify other components
				window.dispatchEvent(new Event("tokenStored"));
			}
		}
	}, [searchParams]);

	return tokensReady;
}

